import { useMemo } from 'react'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type SelfUpsellPricesProps = {
  addon: Plan
  discounts: { discountPercentage: number; amount: number; discountType: string }[] | undefined
}

const useSelfUpsellPrices = ({ addon, discounts }: SelfUpsellPricesProps) => {
  const {
    computeTotalPrice,
    computeCrossSellingTotalPrice,
    period,
    currency,
    seats,
    discount,
    configPlan,
  } = useSelfUpsellContext()

  const variant = useMemo(() => {
    if (!configPlan.variant) return undefined
    return addon.getSpecificVariant(configPlan.variant)
  }, [configPlan.variant, addon])

  const perSeatPrice = variant?.pricing.getSeatsPrice(period)
  const feePrice = variant?.pricing.getFeePrice(period)
  const stairstepPrice = variant?.pricing.getStairstepPrice(period)
  const tieredPrice = variant?.pricing.getTieredPrice(period, seats)

  const baseTotalPrice = computeTotalPrice()

  const crossSellingTotalPrice = computeCrossSellingTotalPrice()

  const annualFeePrice = variant?.pricing.getAnnualFeePrice(period)

  const recalculatedDiscounts = (discounts ?? []).map((d) => {
    if (d.discountPercentage === 100) {
      return {
        ...d,
        amount: crossSellingTotalPrice,
        discountType: d.discountType,
      }
    } else {
      const variation = baseTotalPrice * (d.discountPercentage / 100)
      return {
        ...d,
        amount: variation,
        discountType: d.discountType,
      }
    }
  })

  // Calculate manual discount amount
  const manualDiscountAmount =
    discount && discount.percentage > 0 ? baseTotalPrice * (discount.percentage / 100) : 0

  const totalDiscountAmount = recalculatedDiscounts.reduce(
    (sum: number, d: { amount: number }) => sum + d.amount,
    0
  )

  const totalPriceAfterDiscount = baseTotalPrice + crossSellingTotalPrice - totalDiscountAmount
  const formattedTotalPrice = formatPrice(totalPriceAfterDiscount, currency)

  return {
    perSeatPrice,
    feePrice,
    stairstepPrice,
    tieredPrice,
    baseTotalPrice,
    recalculatedDiscounts,
    manualDiscountAmount,
    totalDiscountAmount,
    totalPriceAfterDiscount,
    formattedTotalPrice,
    annualFeePrice,
  }
}

export default useSelfUpsellPrices
